import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { Bookmark, ChevronLeft, Edit2, Plus, Search, X } from "react-feather";
import { filterSkills, loadLiveSkills, type SkillDescriptor } from "../lib/skills";
import {
  WorkflowError, insertWorkflowSkill, readWorkflow, readWorkflowList, workflowDirty,
  workflowDraft, workflowRequest, workflowRequestScope, workflowSkillSuggestions,
  workflowSlashAt, workflowValidation, type SavedWorkflow, type WorkflowDraft, type WorkflowFetch,
} from "../lib/workflowModel";
import "../styles/workflow-picker.css";

export interface WorkflowPickerProps {
  onInsert: (prompt: string) => void;
  getCurrentDraft: () => string;
  skills: SkillDescriptor[];
  authFetch: WorkflowFetch;
  disabled?: boolean;
  contextKey: string;
  renderTrigger?: (props: { onClick: () => void; disabled: boolean; buttonRef: RefObject<HTMLButtonElement | null> }) => ReactNode;
}
type Confirmation = "close" | "list" | "delete" | "reload" | null;
const jsonOptions = (method: string, body: unknown): RequestInit => ({
  method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
});
const messageFor = (error: unknown) => error instanceof Error ? error.message : "Workflows is unavailable. Please try again.";

/** Text-only catalogue. Inserting calls the composer callback; never the message API. */
export default function WorkflowPicker({ onInsert, skills, authFetch, disabled = false, contextKey, renderTrigger }: WorkflowPickerProps) {
  const uid = useId();
  const [open, setOpen] = useState(false);
  const [openedContext, setOpenedContext] = useState(contextKey);
  const [items, setItems] = useState<SavedWorkflow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [editor, setEditor] = useState<WorkflowDraft | null>(null);
  const [confirm, setConfirm] = useState<Confirmation>(null);
  const [conflict, setConflict] = useState(false);
  const [query, setQuery] = useState("");
  const [availableSkills, setAvailableSkills] = useState(skills);
  const [cursor, setCursor] = useState(0);
  const [suggestionsHidden, setSuggestionsHidden] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [viewport, setViewport] = useState<{ height: number; top: number } | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const dialog = useRef<HTMLDivElement>(null);
  const nameField = useRef<HTMLInputElement>(null);
  const promptField = useRef<HTMLTextAreaElement>(null);
  const confirmField = useRef<HTMLButtonElement>(null);
  const requests = useRef(workflowRequestScope());
  const mutation = useRef(false);
  const mounted = useRef(true);
  const skillsGeneration = useRef(0);
  const sameContext = contextKey === openedContext;
  const slash = editor && !suggestionsHidden ? workflowSlashAt(editor.prompt, cursor) : null;
  const suggestions = slash ? workflowSkillSuggestions(availableSkills, slash.query) : [];
  const visibleItems = items.filter(item => `${item.name}\n${item.prompt}`.toLowerCase().includes(query.toLowerCase()));

  useEffect(() => {
    const scope = requests.current;
    mounted.current = true;
    return () => { mounted.current = false; scope.cancel(); };
  }, []);
  useEffect(() => {
    if (!open || !window.visualViewport) return;
    const visual = window.visualViewport;
    const resize = () => setViewport({ height: visual.height, top: visual.offsetTop });
    resize();
    visual.addEventListener("resize", resize); visual.addEventListener("scroll", resize);
    return () => { visual.removeEventListener("resize", resize); visual.removeEventListener("scroll", resize); };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : trigger.current;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.current?.focus();
    const contain = (event: FocusEvent) => {
      if (event.target instanceof Node && !dialog.current?.contains(event.target)) dialog.current?.focus();
    };
    document.addEventListener("focusin", contain);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener("focusin", contain);
      if (previous?.isConnected && previous !== document.body) previous.focus();
      else trigger.current?.focus({ preventScroll: true });
    };
  }, [open]);
  const editorId = editor?.id;
  useEffect(() => {
    if (confirm) confirmField.current?.focus();
    else if (editorId) nameField.current?.focus();
  }, [editorId, confirm]);
  useEffect(() => {
    if (!open || !editor || !workflowDirty(editor)) return;
    const guard = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [open, editor]);

  async function refresh() {
    const ticket = requests.current.begin();
    setLoading(true); setError("");
    try {
      const rows = readWorkflowList(await workflowRequest(authFetch, "/api/workflows", {}, ticket.signal));
      if (ticket.current()) setItems(rows);
    } catch (err) { if (ticket.current()) setError(messageFor(err)); }
    finally { if (ticket.current()) setLoading(false); }
  }
  function launch() {
    if (disabled) return;
    setOpenedContext(contextKey); setOpen(true); setEditor(null); setConfirm(null);
    setConflict(false); setQuery(""); setNotice(""); setItems([]); setAvailableSkills(skills);
    void refresh();
    const generation = ++skillsGeneration.current;
    void loadLiveSkills(authFetch).then(count => {
      if (mounted.current && count > 0 && generation === skillsGeneration.current) setAvailableSkills(filterSkills(""));
    });
  }
  function close() {
    requests.current.cancel(); ++skillsGeneration.current;
    setOpen(false); setEditor(null); setConfirm(null); setError("");
  }
  function toList() {
    setEditor(null); setConfirm(null); setConflict(false); setError(""); setSuggestionsHidden(true);
    void refresh();
  }
  function requestLeave(destination: "close" | "list") {
    if (mutation.current) return;
    if (editor && workflowDirty(editor)) { setConfirm(destination); return; }
    if (destination === "close") close(); else toList();
  }
  function edit(item?: SavedWorkflow, draft = "") {
    setEditor(workflowDraft(item, draft)); setError(""); setNotice(""); setConfirm(null); setConflict(false);
    setSuggestionsHidden(true); setCursor(0);
  }
  async function save() {
    if (!editor || mutation.current) return;
    const invalid = workflowValidation(editor.name, editor.prompt);
    if (invalid) { setError(invalid); return; }
    const snapshot = editor;
    mutation.current = true; setSaving(true); setError("");
    const ticket = requests.current.begin();
    try {
      const creating = snapshot.version === undefined;
      const body = creating ? { id: snapshot.id, name: snapshot.name, prompt: snapshot.prompt }
        : { version: snapshot.version, name: snapshot.name, prompt: snapshot.prompt };
      const result = await workflowRequest(authFetch, creating ? "/api/workflows" : `/api/workflows/${snapshot.id}`,
        jsonOptions(creating ? "POST" : "PATCH", body), ticket.signal);
      const saved = readWorkflow((result as { workflow?: unknown })?.workflow);
      if (saved.id !== snapshot.id || saved.name !== snapshot.name || saved.prompt !== snapshot.prompt) {
        throw new WorkflowError("This save was recorded, but the workflow changed elsewhere. Your edits are still here; reload the saved version or save them as a new workflow.", 409, "workflow_version_conflict");
      }
      if (ticket.current()) { setNotice("Workflow saved. Your chat draft is unchanged."); toList(); }
    } catch (err) {
      if (ticket.current()) { setError(messageFor(err)); setConflict(err instanceof WorkflowError && [404, 409].includes(err.status) && err.code !== "workflow_limit"); }
    } finally { mutation.current = false; if (mounted.current) setSaving(false); }
  }
  async function remove() {
    if (!editor?.version || mutation.current) return;
    mutation.current = true; setSaving(true); setError("");
    const ticket = requests.current.begin();
    try {
      const result = await workflowRequest(authFetch, `/api/workflows/${editor.id}`, jsonOptions("DELETE", { version: editor.version }), ticket.signal);
      if (!(result && typeof result === "object" && "ok" in result && result.ok === true)) throw new WorkflowError("Could not confirm deletion. Please refresh the list.");
      if (ticket.current()) { setNotice("Workflow deleted. Your chat draft is unchanged."); toList(); }
    } catch (err) {
      if (ticket.current()) { setConfirm(null); setError(messageFor(err)); setConflict(err instanceof WorkflowError && [404, 409].includes(err.status)); }
    } finally { mutation.current = false; if (mounted.current) setSaving(false); }
  }
  async function reloadEditor() {
    if (!editor || mutation.current) return;
    const id = editor.id;
    mutation.current = true; setSaving(true); setError(""); setConfirm(null);
    const ticket = requests.current.begin();
    try {
      const rows = readWorkflowList(await workflowRequest(authFetch, "/api/workflows", {}, ticket.signal));
      const row = rows.find(item => item.id === id);
      if (!row) throw new WorkflowError("This workflow no longer exists. Your text is still here; save it as a new workflow.", 404);
      if (ticket.current()) { setItems(rows); edit(row); }
    } catch (err) { if (ticket.current()) setError(messageFor(err)); }
    finally { mutation.current = false; if (mounted.current) setSaving(false); }
  }
  function insert(item: SavedWorkflow) {
    if (disabled || contextKey !== openedContext) return;
    onInsert(item.prompt);
    close();
  }
  function chooseSkill(skill: SkillDescriptor) {
    if (!editor || !slash) return;
    const next = insertWorkflowSkill(editor.prompt, slash, skill.name);
    if (next.prompt.length > 12_000) { setError("This skill would exceed the 12,000-character limit."); return; }
    setEditor({ ...editor, prompt: next.prompt }); setCursor(next.cursor); setSuggestionsHidden(true);
    requestAnimationFrame(() => { promptField.current?.focus(); promptField.current?.setSelectionRange(next.cursor, next.cursor); });
  }
  function promptKey(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.nativeEvent.isComposing || !suggestions.length) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault(); setHighlight(index => (index + (event.key === "ArrowDown" ? 1 : -1) + suggestions.length) % suggestions.length);
    } else if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault(); chooseSkill(suggestions[Math.min(highlight, suggestions.length - 1)]);
    } else if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); setSuggestionsHidden(true); }
  }
  function dialogKey(event: KeyboardEvent<HTMLDivElement>) {
    event.stopPropagation();
    if (event.key === "Escape") {
      event.preventDefault();
      if (confirm && !saving) setConfirm(null); else requestLeave("close");
    }
    if (event.key === "Tab") {
      const controls = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), textarea:not(:disabled), [tabindex="0"]') ?? []).filter(node => node.getClientRects().length > 0);
      const first = controls[0], last = controls.at(-1);
      if (!first) { event.preventDefault(); dialog.current?.focus(); }
      else if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  }

  return <>
    {renderTrigger ? renderTrigger({ onClick: launch, disabled, buttonRef: trigger }) : <button ref={trigger} type="button" className="wf-trigger" onClick={launch} disabled={disabled}
      aria-label="Open saved workflows" aria-haspopup="dialog" aria-expanded={open}>
      <Bookmark size={16} aria-hidden="true" /><span>Workflows</span>
    </button>}
    {open && createPortal(<div className="wf-overlay" style={viewport ? { top: viewport.top, height: viewport.height, bottom: "auto" } : undefined}
      onDragEnter={event => { event.preventDefault(); event.stopPropagation(); }}
      onDragOver={event => { event.preventDefault(); event.stopPropagation(); }}
      onDrop={event => { event.preventDefault(); event.stopPropagation(); }}
      onPaste={event => event.stopPropagation()}
      onMouseDown={event => { if (event.target === event.currentTarget) requestLeave("close"); }}>
      <div ref={dialog} className="wf-dialog" role="dialog" aria-modal="true" aria-labelledby={`${uid}-title`}
        aria-describedby={`${uid}-description`} tabIndex={-1} onKeyDown={dialogKey}>
        <header className="wf-header">
          {editor && <button className="wf-icon" type="button" disabled={saving} onClick={() => requestLeave("list")} aria-label="Back to saved workflows"><ChevronLeft size={19} /></button>}
          <div><h2 id={`${uid}-title`}>{editor ? editor.version ? "Edit workflow" : "New workflow" : "Workflows"}</h2>
            <p id={`${uid}-description`}>{editor ? "Keep the instructions. Reuse the starting point." : "Insert a saved prompt. Review it, then send."}</p></div>
          <button type="button" className="wf-icon wf-close" disabled={saving} onClick={() => requestLeave("close")} aria-label="Close workflows"><X size={19} /></button>
        </header>
        <div className="wf-body">
          {!sameContext && <p className="wf-alert" role="status">The active chat changed. Your workflow edits are safe here. Close and reopen Workflows before inserting into the new chat.</p>}
          {notice && <p className="wf-notice" role="status">{notice}</p>}
          {error && <div className="wf-alert" role="alert"><p>{error}</p>{!editor && <button type="button" className="wf-button" onClick={() => void refresh()} disabled={loading}>Retry loading</button>}</div>}
          {!editor ? <>
            <label className="wf-search"><Search size={16} aria-hidden="true" /><input value={query} onChange={event => setQuery(event.target.value)} aria-label="Find a workflow" placeholder="Find a workflow…" /></label>
            {loading ? <p className="wf-empty" role="status">Loading workflows…</p> : <div className="wf-list">
              {visibleItems.map(item => <div className="wf-row" key={item.id}>
                <button type="button" className="wf-insert" disabled={disabled || !sameContext} onClick={() => insert(item)} aria-label={`Insert prompt: ${item.name}`}>
                  <span className="wf-copy"><span className="wf-name">{item.name}</span><span className="wf-preview">{item.prompt}</span><span className="wf-insert-hint">Insert prompt</span></span>
                </button>
                <button type="button" className="wf-edit" onClick={() => edit(item)} aria-label={`Edit workflow: ${item.name}`}><Edit2 size={15} aria-hidden="true" /><span>Edit</span></button>
              </div>)}
              {!visibleItems.length && !error && <div className="wf-empty"><Bookmark size={25} aria-hidden="true" /><p>{query ? "No workflows match that search." : "Your repeatable work starts here."}</p><span>{query ? "Try a different name or phrase." : "Save a prompt once, then adapt it for any chat in this app."}</span></div>}
            </div>}
          </> : <div className="wf-editor">
            <label htmlFor={`${uid}-name`}>Workflow name <span aria-hidden="true">{editor.name.length}/80</span></label>
            <input ref={nameField} id={`${uid}-name`} value={editor.name} maxLength={80} disabled={saving || !!confirm}
              autoComplete="off" placeholder="e.g. Prepare client workspace" onChange={event => setEditor({ ...editor, name: event.target.value })} />
            <label htmlFor={`${uid}-prompt`}>Saved prompt <span aria-hidden="true">{editor.prompt.length.toLocaleString()}/12,000</span></label>
            <textarea ref={promptField} id={`${uid}-prompt`} value={editor.prompt} maxLength={12_000} disabled={saving || !!confirm}
              placeholder="Write your instructions. Type / to find a skill…" spellCheck={false} aria-describedby={`${uid}-text-only`}
              aria-controls={suggestions.length ? `${uid}-suggestions` : undefined}
              onChange={event => { setEditor({ ...editor, prompt: event.target.value }); setCursor(event.target.selectionStart); setHighlight(0); setSuggestionsHidden(false); }}
              onSelect={event => { setCursor(event.currentTarget.selectionStart); setHighlight(0); }} onKeyDown={promptKey} />
            {!!suggestions.length && !confirm && <div id={`${uid}-suggestions`} className="wf-suggestions" aria-label="Skill suggestions">
              {suggestions.map((skill, index) => <button type="button" key={skill.name} className={index === highlight ? "is-active" : ""} onClick={() => chooseSkill(skill)}>
                <span>/{skill.name}</span><small>{skill.summary}</small>
              </button>)}
            </div>}
            <p className="wf-help" id={`${uid}-text-only`}>Text and skill commands only. Images, attachments and composer options stay per-message. Inserting a workflow never sends it.</p>
            {/\/notify-julian\b/i.test(editor.prompt) && <p className="wf-alert" role="status">/notify-julian is a composer option, not a saved skill command. Turn on Notify Julian in the composer after inserting; this text does not enable it.</p>}
            {conflict && <div className="wf-conflict"><button type="button" className="wf-button" disabled={saving || !!confirm} onClick={() => setConfirm("reload")}>Reload saved version</button>
              <button type="button" className="wf-button" disabled={saving || !!confirm} onClick={() => { setEditor({ ...workflowDraft(undefined, editor.prompt), name: editor.name }); setConflict(false); setError(""); }}>Save as new workflow</button></div>}
            {confirm && <section className="wf-confirm" aria-label={confirm === "delete" ? "Confirm deletion" : "Confirm discarding edits"}>
              <h3>{confirm === "delete" ? "Delete this workflow?" : confirm === "reload" ? "Replace your edits with the saved version?" : "Discard your unsaved edits?"}</h3>
              <p>{confirm === "delete" ? "This removes it from every chat in this app. Existing messages and your current draft are unchanged." : "Your current workflow edits have not been saved."}</p>
              <div className="wf-confirm-actions"><button ref={confirmField} type="button" className="wf-button" disabled={saving} onClick={() => setConfirm(null)}>{confirm === "delete" ? "Keep workflow" : "Keep editing"}</button>
                <button type="button" className="wf-button wf-danger" disabled={saving} onClick={() => {
                  if (confirm === "delete") void remove(); else if (confirm === "reload") void reloadEditor(); else if (confirm === "close") close(); else toList();
                }}>{saving ? "Working…" : confirm === "delete" ? "Delete workflow" : confirm === "reload" ? "Discard edits and reload" : "Discard edits"}</button></div>
            </section>}
          </div>}
        </div>
        <footer className="wf-footer">
          {!editor ? <><button type="button" className="wf-new" disabled={loading || items.length >= 100} onClick={() => edit()}><Plus size={17} aria-hidden="true" />New workflow</button>
            <p>Saved across chats in this app · text only{items.length >= 100 ? " · 100-workflow limit reached" : ""}</p></>
            : <>{editor.version && <button type="button" className="wf-button wf-delete" disabled={saving || !!confirm} onClick={() => setConfirm("delete")}>Delete</button>}
              <div className="wf-footer-actions">
                <button type="button" className="wf-button" disabled={saving || !!confirm} onClick={() => requestLeave("list")}>Cancel</button>
                <button type="button" className="wf-button wf-primary" disabled={saving || !!confirm} onClick={() => void save()}>{saving ? "Saving…" : "Save workflow"}</button>
              </div></>}
        </footer>
      </div>
    </div>, document.body)}
  </>;
}
